import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { LoginService } from '../services/login.service';
import { GlobalServicesService } from '../services/global-services.service';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError, retry, retryWhen, take, delay, concatMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {

  private failedAttempts = 0;
  private readonly maxAttempts = 2;
  constructor(
    private loginService: LoginService,
    private route: Router,
    private globalService: GlobalServicesService
  ) { }

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> | boolean | UrlTree {
    if (state.url === '/logout') {
      this.logout();
      return false;
    }
    
    const requiredPrivilege = next.data['privilege'];
    return this.globalService.getUserProfileFastApi().pipe(
      retryWhen(errors => 
        errors.pipe(
          concatMap((error, index) => {
            this.failedAttempts = index + 1;
            if (this.failedAttempts >= this.maxAttempts) {
              return throwError(() => error);
            }
            return of(error).pipe(delay(1000));
          }),
          take(this.maxAttempts)
        )
      ),
      map((res: any) => {
        this.failedAttempts = 0;
        const userPrivileges = res.body.auth_user_permissions || [];
        this.globalService.privilegeDetails = new Set(userPrivileges);
        this.globalService.privilegeDetailsSubject.next(this.globalService.privilegeDetails);
        this.globalService.companyMetadataSubject.next(res.body.company_metadata);    
        this.globalService.setUserMeta(
          res.body.auth_user_details,
          res.body.auth_user_role_details
        );
        const company_settings = res.body.company_settings;
        const letter = ['D', 'Y'];
  
        company_settings.date_format = company_settings.date_format?.toString().replace(
          new RegExp(letter.join('|'), 'g'),
          (match: string) => match.toLowerCase()
        );
        company_settings.time_format = company_settings.time_format == '24' ? 'HH:mm' : 'hh:mm';    
        localStorage.setItem('company-details', JSON.stringify({
          company_metadata: res.body.company_metadata,
          company_settings: res.body.company_settings,
          company_details: res.body.company_details
        }));    
        let hasAccess = true;
        if (requiredPrivilege) {
          if (Array.isArray(requiredPrivilege)) {
            hasAccess = requiredPrivilege.some(priv => userPrivileges.includes(priv));
          } else {
            hasAccess = userPrivileges.includes(requiredPrivilege);
          }
        }
        const dashboardCheck = next.data['dashboardCheck'] === true;
        if (dashboardCheck) {
          const dashboardEnabled = res.body.company_metadata?.company_config?.dashboard ?? false;
          if (!dashboardEnabled) {
            if (userPrivileges.includes('event.view') || userPrivileges.includes('event.create')) {
              this.route.navigate(['/events']);
            } else {
              this.route.navigate(['/settings']);
            }
            return false;
          }
        }
        if (!hasAccess) {
          this.route.navigate(['/error']);
        }
    
        return hasAccess;
      }),
      catchError((error) => {
        const errorResponse:any = error.error
        const isLoggedIn = localStorage.getItem('tokenFastApi') != null;
        if (this.failedAttempts >= this.maxAttempts) {
          this.logout();
          return of(false);
        }
        if (isLoggedIn) {
          if (errorResponse && errorResponse.detail === 'Session has expired') {
            this.logout();
          } else {
            this.route.navigate(['/error']);
          }
        } else {
          this.logout();
        }
        return of(false);
      })
    );
  }
  
  logout() {
    this.failedAttempts = 0;
    this.removeKeys();
    this.route.navigate(['/login']);
  }
  
  removeKeys() {
    for (var attr in localStorage) {
        localStorage.removeItem(attr);
    }
  }
}
